import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

interface ForceDeleteModalProps {
  templateTitle: string;
  warningData: {
    submissionCount: number;
    affectedSuppliers: Array<{
      id: string;
      supplierName: string;
      supplierEmail: string;
      status: string;
      createdAt: string;
    }>;
  };
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const ForceDeleteModal: React.FC<ForceDeleteModalProps> = ({
  templateTitle,
  warningData,
  onConfirm,
  onClose
}) => {
  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.01 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.99 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.99 }}
        transition={{ duration: 0.02 }}
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Warnung: Fragebogen ist in Verwendung</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-1"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Der Fragebogen <strong>"{templateTitle}"</strong> kann nicht gelöscht werden, da er noch von{' '}
            <strong>{warningData.submissionCount} Lieferanten</strong> verwendet wird.
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-yellow-800 mb-2">Betroffene Lieferanten:</h4>
            <div className="max-h-32 overflow-y-auto">
              {warningData.affectedSuppliers.slice(0, 5).map((supplier, index) => (
                <div key={supplier.id} className="flex justify-between text-sm text-yellow-700 py-1">
                  <span>{supplier.supplierName || supplier.supplierEmail}</span>
                  <span className="capitalize">{supplier.status}</span>
                </div>
              ))}
              {warningData.affectedSuppliers.length > 5 && (
                <div className="text-sm text-yellow-600 font-medium mt-2">
                  ... und {warningData.affectedSuppliers.length - 5} weitere
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              <strong>Erzwungenes Löschen:</strong> Wenn Sie fortfahren, werden der Fragebogen UND alle zugehörigen 
              Lieferanten-Submissions unwiderruflich gelöscht. Die Lieferanten verlieren dadurch alle ihre Antworten!
            </p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
          >
            Abbrechen
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium flex items-center justify-center space-x-2"
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>Trotzdem löschen</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ForceDeleteModal;
